import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { BarChart, LineChart, PieChart, AreaChart } from './charts';
import ModuleContainer from './ModuleContainer';
import { TableModule, CardModule, MetricsModule } from './modules';
import ThemeSelector from './ThemeSelector';

const PersonalFinanceDashboard = () => {
  const { theme } = useTheme();

  useEffect(() => {
    document.title = 'Sydney Ferris - Personal Finance';
  }, []);

  const [financeData, setFinanceData] = useState({
    transactions: [],
    accounts: [],
    budgets: [],
    investments: [],
    expenses: [],
    income: []
  });

  // Sample personal finance data
  const sampleFinanceData = {
    transactions: [
      { date: '2024-01-01', description: 'Grocery Store', amount: -125.50, category: 'Food', type: 'expense' },
      { date: '2024-01-02', description: 'Salary Deposit', amount: 3500.00, category: 'Income', type: 'income' },
      { date: '2024-01-03', description: 'Electric Bill', amount: -89.00, category: 'Utilities', type: 'expense' },
      { date: '2024-01-04', description: 'Restaurant', amount: -45.00, category: 'Food', type: 'expense' },
      { date: '2024-01-05', description: 'Gas Station', amount: -65.00, category: 'Transportation', type: 'expense' },
      { date: '2024-01-06', description: 'Freelance Work', amount: 750.00, category: 'Income', type: 'income' },
      { date: '2024-01-07', description: 'Netflix', amount: -15.99, category: 'Entertainment', type: 'expense' },
      { date: '2024-01-08', description: 'Gym Membership', amount: -49.99, category: 'Health', type: 'expense' },
    ],
    accounts: [
      { name: 'Checking Account', balance: 5234.56, type: 'checking', bank: 'Chase' },
      { name: 'Savings Account', balance: 12500.00, type: 'savings', bank: 'Chase' },
      { name: 'Credit Card', balance: -1250.75, type: 'credit', bank: 'Visa' },
      { name: 'Investment Account', balance: 45678.90, type: 'investment', bank: 'Fidelity' },
    ],
    budgets: [
      { category: 'Food', budgeted: 600, spent: 425, remaining: 175 },
      { category: 'Transportation', budgeted: 200, spent: 180, remaining: 20 },
      { category: 'Entertainment', budgeted: 150, spent: 95, remaining: 55 },
      { category: 'Utilities', budgeted: 300, spent: 289, remaining: 11 },
      { category: 'Health', budgeted: 100, spent: 75, remaining: 25 },
    ],
    monthlyExpenses: [
      { month: 'Jan', amount: 2450 },
      { month: 'Feb', amount: 2300 },
      { month: 'Mar', amount: 2600 },
      { month: 'Apr', amount: 2200 },
      { month: 'May', amount: 2800 },
      { month: 'Jun', amount: 2500 },
    ],
    monthlyIncome: [
      { month: 'Jan', amount: 4250 },
      { month: 'Feb', amount: 4250 },
      { month: 'Mar', amount: 4500 },
      { month: 'Apr', amount: 4250 },
      { month: 'May', amount: 5000 },
      { month: 'Jun', amount: 4250 },
    ],
    categoryBreakdown: [
      { category: 'Housing', amount: 1200, percentage: 35 },
      { category: 'Food', amount: 600, percentage: 18 },
      { category: 'Transportation', amount: 400, percentage: 12 },
      { category: 'Utilities', amount: 300, percentage: 9 },
      { category: 'Entertainment', amount: 200, percentage: 6 },
      { category: 'Health', amount: 150, percentage: 4 },
      { category: 'Other', amount: 580, percentage: 16 },
    ],
    netWorth: [
      { month: 'Jan', value: 45000 },
      { month: 'Feb', value: 46200 },
      { month: 'Mar', value: 47800 },
      { month: 'Apr', value: 48500 },
      { month: 'May', value: 51200 },
      { month: 'Jun', value: 53400 },
    ]
  };

  useEffect(() => {
    setFinanceData(sampleFinanceData);
  }, []);

  const calculateTotalBalance = () => {
    if (!financeData.accounts || !Array.isArray(financeData.accounts)) return 0;
    return financeData.accounts.reduce((total, account) => total + (account.balance || 0), 0);
  };

  const calculateMonthlySavings = () => {
    if (!financeData.monthlyIncome || !Array.isArray(financeData.monthlyIncome) || 
        !financeData.monthlyExpenses || !Array.isArray(financeData.monthlyExpenses)) {
      return 0;
    }
    const latestIncome = financeData.monthlyIncome[financeData.monthlyIncome.length - 1]?.amount || 0;
    const latestExpenses = financeData.monthlyExpenses[financeData.monthlyExpenses.length - 1]?.amount || 0;
    return latestIncome - latestExpenses;
  };

  const getBudgetStatus = (budget) => {
    if (!budget || typeof budget.spent !== 'number' || typeof budget.budgeted !== 'number' || budget.budgeted === 0) {
      return { status: 'unknown', color: 'gray' };
    }
    const percentage = (budget.spent / budget.budgeted) * 100;
    if (percentage > 90) return { status: 'danger', color: 'red' };
    if (percentage > 75) return { status: 'warning', color: 'yellow' };
    return { status: 'good', color: 'green' };
  };

  return (
    <div className="min-h-screen theme-bg-secondary">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold theme-primary">Personal Finance Dashboard</h1>
              <p className="theme-secondary mt-1 text-sm sm:text-base">
                Track your income, expenses, budgets, and net worth
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeSelector />
              <button
                onClick={() => setFinanceData(sampleFinanceData)}
                className="px-3 py-2 sm:px-4 theme-accent text-white rounded-lg hover:opacity-80 text-xs sm:text-sm"
              >
                Refresh Data
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <ModuleContainer
            moduleType="Metrics"
            moduleId="total-balance"
            title="Total Balance"
            data={[
              { value: `$${calculateTotalBalance().toFixed(2)}`, label: 'All Accounts' },
              { value: `$${calculateMonthlySavings().toFixed(2)}`, label: 'Monthly Savings', change: calculateMonthlySavings() > 0 ? 12 : -5 }
            ]}
            config={{ layout: 'vertical' }}
          />

          <ModuleContainer
            moduleType="Metrics"
            moduleId="monthly-summary"
            title="Monthly Summary"
            data={[
              {
                value: `$${(financeData.monthlyIncome && financeData.monthlyIncome.length > 0 ? financeData.monthlyIncome[financeData.monthlyIncome.length - 1]?.amount || 0 : 0).toFixed(0)}`,
                label: 'Income'
              },
              {
                value: `$${(financeData.monthlyExpenses && financeData.monthlyExpenses.length > 0 ? financeData.monthlyExpenses[financeData.monthlyExpenses.length - 1]?.amount || 0 : 0).toFixed(0)}`,
                label: 'Expenses',
                change: -5
              }
            ]}
            config={{ layout: 'vertical' }}
          />

          <ModuleContainer
            moduleType="Metrics"
            moduleId="budget-health"
            title="Budget Health"
            data={[
              { value: financeData.budgets ? `${financeData.budgets.filter(b => getBudgetStatus(b).status === 'good').length}/${financeData.budgets.length}` : '0/0', label: 'On Track' },
              { value: financeData.budgets && financeData.budgets.length > 0 ? `${Math.round((financeData.budgets.reduce((sum, b) => sum + (b.spent || 0), 0) / financeData.budgets.reduce((sum, b) => sum + (b.budgeted || 0), 0)) * 100)}%` : '0%', label: 'Budget Used', change: -8 }
            ]}
            config={{ layout: 'vertical' }}
          />

          <ModuleContainer
            moduleType="Metrics"
            moduleId="net-worth"
            title="Net Worth"
            data={[
              {
                value: `$${(financeData.netWorth && financeData.netWorth.length > 0 ? financeData.netWorth[financeData.netWorth.length - 1]?.value || 0 : 0).toFixed(0)}`,
                label: 'Current',
                change: 8
              }
            ]}
            config={{ layout: 'vertical' }}
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <ModuleContainer
            moduleType="Chart"
            moduleId="income-vs-expenses"
            title="Income vs Expenses Trend"
            data={
              <div className="h-48 sm:h-64">
                <LineChart
                  title=""
                  moduleId="income-expenses"
                  dataIndex={0}
                  showLabels={true}
                  animate={true}
                />
              </div>
            }
            config={{ showFooter: true }}
          />

          <ModuleContainer
            moduleType="Chart"
            moduleId="expense-breakdown"
            title="Expense Categories"
            data={
              <div className="h-48 sm:h-64">
                <PieChart
                  title=""
                  moduleId="categories"
                  dataIndex={0}
                  showLabels={true}
                  showLegend={true}
                />
              </div>
            }
            config={{ showFooter: true }}
          />
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <ModuleContainer
            moduleType="Chart"
            moduleId="net-worth-trend"
            title="Net Worth Trend"
            data={
              <div className="h-48 sm:h-64">
                <AreaChart
                  title=""
                  moduleId="networth"
                  dataIndex={0}
                  showLabels={true}
                  opacity={0.3}
                />
              </div>
            }
            config={{ showFooter: true }}
          />

          <ModuleContainer
            moduleType="Chart"
            moduleId="monthly-comparison"
            title="Monthly Comparison"
            data={
              <div className="h-48 sm:h-64">
                <BarChart
                  title=""
                  moduleId="monthly"
                  dataIndex={0}
                  showLabels={true}
                  animate={true}
                />
              </div>
            }
            config={{ showFooter: true }}
          />
        </div>

        {/* Budget Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <ModuleContainer
            moduleType="Table"
            moduleId="budget-tracking"
            title="Budget Tracking"
            data={financeData.budgets ? financeData.budgets.map(budget => ({
              ...budget,
              status: getBudgetStatus(budget).status,
              percentage: budget.budgeted > 0 ? ((budget.spent / budget.budgeted) * 100).toFixed(1) + '%' : '0%'
            })) : []}
            config={{
              columns: [
                { key: 'category', label: 'Category' },
                { key: 'budgeted', label: 'Budgeted' },
                { key: 'spent', label: 'Spent' },
                { key: 'remaining', label: 'Remaining' },
                { key: 'percentage', label: 'Used' }
              ],
              sortable: true
            }}
          />

          <ModuleContainer
            moduleType="Table"
            moduleId="recent-transactions"
            title="Recent Transactions"
            data={financeData.transactions ? financeData.transactions.slice(0, 5) : []}
            config={{
              columns: [
                { key: 'date', label: 'Date' },
                { key: 'description', label: 'Description' },
                { key: 'category', label: 'Category' },
                { key: 'amount', label: 'Amount' }
              ],
              sortable: true
            }}
          />
        </div>

        {/* Accounts Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <ModuleContainer
            moduleType="Card"
            moduleId="accounts-overview"
            title="Accounts Overview"
            data={financeData.accounts ? financeData.accounts.map(account => ({
              ...account,
              title: account.name,
              description: `${account.type.charAt(0).toUpperCase() + account.type.slice(1)} - ${account.bank}`,
              value: `$${Math.abs(account.balance).toFixed(2)}`,
              trend: account.balance >= 0 ? 'up' : 'down',
              trendValue: account.balance >= 0 ? 'Positive' : 'Negative'
            })) : []}
            config={{
              layout: 'grid',
              size: 'medium'
            }}
          />

          <ModuleContainer
            moduleType="Card"
            moduleId="budget-alerts"
            title="Budget Alerts"
            data={financeData.budgets ? financeData.budgets
              .filter(budget => getBudgetStatus(budget).status === 'warning' || getBudgetStatus(budget).status === 'danger')
              .map(budget => ({
                title: budget.category,
                description: `${budget.budgeted > 0 ? ((budget.spent / budget.budgeted) * 100).toFixed(0) : '0'}% of budget used`,
                value: `$${Math.abs(budget.remaining)}`,
                trend: budget.spent > budget.budgeted ? 'down' : 'up',
                trendValue: `${Math.abs(budget.budgeted - budget.spent)}`
              })) : []}
            config={{
              layout: 'list',
              size: 'small'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceDashboard;
